import { useEffect, useState } from "react";
import { useAxios } from "../../../hooks/axios";
import useUser, {
  useRenderLanguage,
  useUserMetadata,
} from "../../../hooks/user";
import Loader from "../../common/Loader";
import { useEvent } from "../../../hooks/event";
import EventWaitingRoom from "./EventWaitingRoom";
import { usePublisherToken } from "../../../hooks/token";
import { LiveKitRoom, RoomAudioRenderer } from "@livekit/components-react";
import SupportDialog from "../../common/dialogs/SupportDialog";
import UserLanguageSelect from "../../common/UserLanguageSelect";
import Captions from "../../common/Captions";
import classNames from "./EventAttendee.module.css";
import Voice from "../../common/Voice";
import { useRoom, useWakeLock } from "../../context/room/room";
import { useDefaultSettings } from "../../../hooks/settings";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ConfirmLeaveDialog from "../../common/dialogs/ConfirmLeaveDialog";
import SettingsDialog from "../../common/dialogs/SettingsDialog";
import { IoHandRightOutline, IoSettingsOutline } from "react-icons/io5";
import Button from "../../common/Button";
import EventTitles from "./EventTitles";
import Chat from "../../common/Chat";
import useLayout, { useIsMobile } from "../../../hooks/layout";
import interpreterWaveformImg from "../../../assets/event/interpreter-waveform.svg";
import { useInterpreterInRoomAndUnmuted } from "../../../hooks/voice";
import Header from "../../Header";
import OverflowPopover from "./OverflowPopover";
import { useListenForChatMessages } from "../../../hooks/chat";
import useCaptions from "../../../hooks/captions";
import { initialCaptionsState } from "../../../types";

function ViewerScreen() {
  // Sync metadata with livekit
  useUserMetadata();
  useDefaultSettings();
  const { room, organization, loading } = useRoom();

  useWakeLock();

  const renderLanguage = useRenderLanguage();
  const interpreterInRoomAndUnmuted = useInterpreterInRoomAndUnmuted();

  const [showQna, setShowQna] = useState(false);
  useListenForChatMessages();

  const [layout] = useLayout();
  const [user] = useUser();

  const isMobile = useIsMobile();

  if (!room || !organization || loading) {
    return <Loader />;
  }

  return (
    <>
      <RoomAudioRenderer />
      <div className={classNames.ViewerScreen}>
        <div className={classNames.Panels}>
          <div className={classNames.TitlesAndCaptions}>
            <EventTitles room={room} />
            {(user.metadata.voice !== undefined &&
              !layout.settings.textTranslationEnabled) ||
            interpreterInRoomAndUnmuted ? (
              <div className={classNames.NoCaptions}>
                <h2>Real-time live interpretation in progress...</h2>
                <img src={interpreterWaveformImg} alt="Waveform" />
              </div>
            ) : (
              <Captions
                showSpeakerName={room.num_speakers !== 1}
                renderLanguage={renderLanguage}
                maxWidth="75%"
              />
            )}
          </div>
          <div
            className={classNames.Qna}
            style={{ display: !showQna ? "none" : undefined }}
          >
            <Chat
              canAskQuestions={true}
              mode="qna"
              open={showQna}
              onOpenChange={() => setShowQna(false)}
            />
          </div>
        </div>
        <div className={classNames.Controls}>
          {!isMobile ? (
            <div>
              <UserLanguageSelect languages={room.languages} />
              {organization.voice_translation_enabled && (
                <Voice hideText={false} />
              )}
              {room.qna && (
                <Button
                  color={showQna ? "white" : "gray"}
                  borderColor={showQna ? "primary" : "dark-gray"}
                  backgroundColor="black"
                  style={{
                    position: "relative",
                  }}
                  onClick={() => setShowQna((prevState) => !prevState)}
                >
                  <IoHandRightOutline />
                  Q&A
                </Button>
              )}
            </div>
          ) : (
            <>
              <UserLanguageSelect languages={room.languages} />
              {organization.voice_translation_enabled && (
                <Voice hideText={true} />
              )}
              {room.qna && (
                <Button
                  color={showQna ? "white" : "gray"}
                  borderColor={showQna ? "primary" : "dark-gray"}
                  backgroundColor="black"
                  style={{
                    position: "relative",
                  }}
                  onClick={() => setShowQna((prevState) => !prevState)}
                >
                  <IoHandRightOutline />
                </Button>
              )}
            </>
          )}

          {!isMobile ? (
            <div>
              <SettingsDialog
                trigger={
                  <Button
                    color="gray"
                    borderColor="dark-gray"
                    backgroundColor="black"
                  >
                    <IoSettingsOutline />
                    Settings
                  </Button>
                }
                hiddenCategories={["audio", "video", "voice-avatar"]}
              />
              <ConfirmLeaveDialog link={`/event/${room.id}/survey`} />
            </div>
          ) : (
            <>
              <OverflowPopover type="attendee" organization={organization} />
              <ConfirmLeaveDialog link={`/event/${room.id}/survey`} />
            </>
          )}
        </div>
      </div>
    </>
  );
}

function InEvent() {
  const axios = useAxios();
  const { token, error: tokenError } = usePublisherToken(axios);
  const { room } = useRoom();

  const [showSupportDialog, setShowSupportDialog] = useState(false);

  const navigate = useNavigate();
  useEffect(() => {
    if (!room) {
      return;
    }
    if (tokenError && tokenError.response?.data.message === "EVENT_ENDED") {
      toast(
        <div className="Toast">
          <div>{"This event has ended, thanks for joining us"}</div>
        </div>
      );
      navigate(`/event/${room.id}/survey`);
    }
  }, [tokenError, navigate, room]);

  return (
    <>
      <SupportDialog
        open={showSupportDialog}
        onOpenChange={setShowSupportDialog}
      />
      <div className={classNames.EventAttendee}>
        <Header />
        <LiveKitRoom
          audio={false}
          video={false}
          connect={true}
          token={token}
          connectOptions={{ autoSubscribe: false }}
          serverUrl={import.meta.env.VITE_LIVEKIT_URL}
          style={{ width: "100%", height: "100%" }}
        >
          <ViewerScreen />
        </LiveKitRoom>
      </div>
    </>
  );
}

export default function EventAttendee() {
  const [, setUser] = useUser();
  const [, setCaptions] = useCaptions();

  const [event] = useEvent();
  const { room, loading } = useRoom();

  // Join meeting as viewer
  useEffect(() => {
    setUser((prevState) => ({
      ...prevState,
      metadata: {
        ...prevState.metadata,
        participantType: "viewer",
      },
    }));
    return () => {
      // Set participant type to undefined when they leave the event
      setUser((prevState) => ({
        ...prevState,
        metadata: {
          ...prevState.metadata,
          participantType: undefined,
        },
      }));
      setCaptions(initialCaptionsState);
    };
  }, [setUser, setCaptions]);

  if (!room || loading) {
    return <Loader />;
  }

  if (!event.joined) {
    return <EventWaitingRoom />;
  }

  return <InEvent />;
}
